/* eslint-disable camelcase */
import { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Grid, Typography, Autocomplete, Badge, Button } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQuery, api } from "../../../api"

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StorefrontIcon from "@mui/icons-material/Storefront"
import PrintIcon from "@mui/icons-material/Print"
import { GlobalContext } from "../../../contexts/GlobalContext"
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"

// Importamos los componentes desde la carpeta Crear
import CatalogoProductos from "../Crear/components/CatalogoProductos"
import CarritoResumen from "../Crear/components/CarritoResumen"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const BuscarSolicitudIngresoCliente = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const globalContext = useContext(GlobalContext)

  const [carritoOpen, setCarritoOpen] = useState(false)
  const fechaHoy = new Date().toISOString().split("T")[0]

  // Esta pantalla siempre es de Solo Lectura
  const isReadOnly = true

  // Paso 1: Estado unificado
  const [formData, setFormData] = useState({
    sgasoling: "",
    sgastatus: "",
    tsolcodigo: "",
    tsolInEg: "",
    ciaaproautinsol: 0,
    motcodigo: "",
    sgadescri: "",
    sgacomenllegada: "",
    clicodigo: "",
    procodigo: "",
    sgafecllegada: fechaHoy,
    sgahorllegada: "",
    detalle: [],
  })

  // Retorno de seguridad si recargan la página
  useEffect(() => {
    if (!state?.sgasoling) navigate(-1)
  }, [state, navigate])

  // ==========================================================================
  // 1. OBTENER DATOS DE LA SOLICITUD EXISTENTE
  // ==========================================================================
  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getSolIngCliByIdBuscar", state?.sgasoling],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudIngCli/getSolicitudById", { sgasoling: state.sgasoling })

        const findSolData = (obj) => {
          if (!obj) return null
          if (obj.sgasoling) return obj
          if (obj.data) return findSolData(obj.data)
          return null
        }

        return findSolData(response) || {}
      } catch (error) {
        return null
      }
    },
    enabled: !!state?.sgasoling,
  })

  // ==========================================================================
  // 2. CARGA DE CATÁLOGOS
  // ==========================================================================
  const { data: catData, isLoading: loadCat } = useQuery({
    queryKey: ["catalogosIngresoClienteBuscar"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudIngCli/getCatalogosIngreso", {})

        const findCatalogos = (obj) => {
          if (!obj) return {}
          if (obj.catalogo_productos || obj.motivos) return obj
          if (obj.data) return findCatalogos(obj.data)
          return {}
        }

        return findCatalogos(response)
      } catch (error) {
        return { tipos_solicitud: [], motivos: [], catalogo_productos: [], proveedores: [], cliente_asignado: null }
      }
    },
    staleTime: 0,
  })

  // Mapeo defensivo
  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []
  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaMotivos = extractArray(catData?.motivos)
  const listaProveedores = extractArray(catData?.proveedores)
  const catalogoProductos = extractArray(catData?.catalogo_productos)
  const clienteAsignado = catData?.cliente_asignado || null

  // Sincronización de Datos al Formulario
  useEffect(() => {
    if (solData && solData.sgasoling) {
      const fechaLlegadaSQL = solData.sgafecllegada ? solData.sgafecllegada.split(" ")[0] : fechaHoy
      let horaLlegadaSQL = ""
      if (solData.sgahorllegada) {
        const partesHora = solData.sgahorllegada.split(" ")[1]
        if (partesHora) horaLlegadaSQL = partesHora.substring(0, 5)
      }

      setFormData({
        sgasoling: solData.sgasoling,
        sgastatus: solData.sgastatus || "DESCONOCIDO",
        tsolcodigo: solData.tsolcodigo || "",
        tsolInEg: "",
        ciaaproautinsol: 0,
        motcodigo: solData.motcodigo || "",
        sgadescri: solData.sgadescri || "",
        sgacomenllegada: solData.sgacomenllegada || "",
        clicodigo: solData.clicodigo || "",
        procodigo: solData.procodigo || "",
        sgafecllegada: fechaLlegadaSQL,
        sgahorllegada: horaLlegadaSQL,
        detalle: solData.detalle || [],
      })
    }
  }, [solData, fechaHoy])

  useEffect(() => {
    if (clienteAsignado?.id && formData.clicodigo !== clienteAsignado.id && !formData.sgasoling) {
      setFormData((prev) => ({ ...prev, clicodigo: clienteAsignado.id }))
    }
  }, [clienteAsignado, formData.clicodigo, formData.sgasoling])

  // ==========================================================================
  // IMPRESIÓN DEL REPORTE
  // ==========================================================================
  const handleImprimirReporte = () => {
    const doc = new JsPDF("p", "pt", "a4")
    const nombreCiaDinamic =
      globalContext?.ciaSeleccionada?.label ||
      globalContext?.ciaSeleccionada?.cianombre ||
      globalContext?.empresaSeleccionada?.label ||
      globalContext?.seleccion?.cianombre ||
      "SISTEMA LOGÍSTICO MERCATTI"
    const tipoSol = listaTiposSolicitud.find((t) => t.tsolcodigo === formData.tsolcodigo)?.label || ""
    const motivo = listaMotivos.find((m) => m.id === formData.motcodigo)?.label || ""
    const proveedor = listaProveedores.find((p) => p.id === formData.procodigo)?.label || "N/A"
    const truncateTxt = (txt, max) => (txt.length > max ? txt.substring(0, max) + "..." : txt)

    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135)
    doc.text(nombreCiaDinamic, 40, 40)
    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text(`SOLICITUD DE INGRESO NRO: ${formData.sgasoling}`, 40, 65)

    // Estado de la solicitud en el PDF
    doc.setFontSize(12)
    doc.setTextColor(211, 47, 47)
    doc.text(`Estado Actual: ${formData.sgastatus}`, 40, 80)

    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)
    doc.text(truncateTxt(`Cliente Asociado: ${clienteAsignado?.label || formData.clicodigo}`, 70), 40, 105)
    doc.text(truncateTxt(`Tipo de Solicitud: ${tipoSol}`, 70), 40, 120)
    doc.text(truncateTxt(`Motivo de Ingreso: ${motivo}`, 70), 40, 135)
    doc.text(truncateTxt(`Proveedor Remitente: ${proveedor}`, 70), 40, 150)
    doc.text(
      `Fecha/Hora Llegada: ${dayjs(formData.sgafecllegada).format("DD/MM/YYYY")} a las ${formData.sgahorllegada}`,
      555,
      105,
      { align: "right" },
    )
    doc.text(`Fecha Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 555, 120, { align: "right" })
    doc.setFont("helvetica", "bold")
    doc.text("Descripción General:", 40, 170)
    doc.setFont("helvetica", "normal")
    const splitDesc = doc.splitTextToSize(formData.sgadescri, 500)
    doc.text(splitDesc, 40, 185)

    const tableColumn = ["Ítem", "Código", "Descripción", "Cantidad Solicitada"]
    const tableRows = formData.detalle.map((prod, idx) => [
      idx + 1,
      prod.artcodigo,
      prod.artdescri,
      prod.cantidad_solicitada,
    ])

    autoTable(doc, {
      startY: 200 + splitDesc.length * 10,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center", cellWidth: 40 },
        1: { halign: "left", cellWidth: 100 },
        3: { halign: "center", cellWidth: 100, fontStyle: "bold" },
      },
    })
    doc.save(`Solicitud_Ingreso_${formData.sgasoling}.pdf`)
  }

  const loadingGlobal = loadCat || loadSol
  const totalItemsEnCarrito = formData.detalle.reduce(
    (acc, item) => acc + (parseFloat(item.cantidad_solicitada) > 0 ? 1 : 0),
    0,
  )

  // Determinar color del estado visualmente
  const getColorStatus = (status) => {
    if (status === "PENDIENTE") return "#ed6c02"
    if (status === "APROBADA") return "#1976d2"
    if (status === "CERRADA") return "#2e7d32"
    if (status === "NEGADA") return "#d32f2f"
    return "#9c27b0"
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<PrintIcon />}
            onClick={handleImprimirReporte}
            disabled={loadingGlobal || !formData.sgasoling}
            sx={{ fontWeight: "bold", borderRadius: 8, px: 4 }}
          >
            Imprimir Comprobante
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              <Badge badgeContent={totalItemsEnCarrito} color="error">
                <ShoppingCartIcon />
              </Badge>
            }
            onClick={() => setCarritoOpen(true)}
            disabled={loadingGlobal}
            sx={{ fontWeight: "bold", borderRadius: 8, px: 4 }}
          >
            Ver Solicitud Completa
          </Button>
        </Box>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "0 30px 10px 30px" }}>
          <StorefrontIcon sx={{ fontSize: 40, color: "#196C87", mr: 1 }} />
          <Typography variant="h4" color="primary" fontWeight="bold">
            Consultar Solicitud de Ingreso {formData.sgasoling ? `- ${formData.sgasoling}` : ""}
          </Typography>
        </div>

        {/* Banner de Estado */}
        <Box display="flex" justifyContent="center" mb={4}>
          <Paper
            elevation={1}
            sx={{ bgcolor: getColorStatus(formData.sgastatus), color: "white", px: 4, py: 1, borderRadius: 2 }}
          >
            <Typography variant="h6" fontWeight="bold">
              ESTADO ACTUAL: {formData.sgastatus}
            </Typography>
          </Paper>
        </Box>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, background: "white", mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ borderBottom: "2px solid #196C87", pb: 1, mb: 3 }}
            >
              Información de Ingreso
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cliente Asociado"
                  value={clienteAsignado ? clienteAsignado.label : formData.clicodigo || "Cargando..."}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.tsolcodigo) === String(formData.tsolcodigo)) ||
                    (formData.tsolcodigo
                      ? { id: formData.tsolInEg, tsolcodigo: formData.tsolcodigo, label: "Cargando..." }
                      : null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled
                  options={listaProveedores}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaProveedores.find((p) => String(p.id) === String(formData.procodigo)) ||
                    (formData.procodigo ? { id: formData.procodigo, label: "Cargando..." } : null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Proveedor" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaMotivos.find((c) => String(c.id) === String(formData.motcodigo)) ||
                    (formData.motcodigo ? { id: formData.motcodigo, label: "Cargando..." } : null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo de Ingreso" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Llegada"
                  InputLabelProps={{ shrink: true }}
                  value={formData.sgafecllegada}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Llegada"
                  InputLabelProps={{ shrink: true }}
                  value={formData.sgahorllegada}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  disabled
                  fullWidth
                  label="Descripción / Observación"
                  value={formData.sgadescri}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled
                  fullWidth
                  label="Instrucciones para la Recepción"
                  value={formData.sgacomenllegada}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Galería Visual en Solo Lectura Total */}
          <div style={{ pointerEvents: "none", opacity: 0.8 }}>
            <CatalogoProductos
              catalogo={catalogoProductos}
              carrito={formData.detalle}
              setFormData={() => {}}
              loading={loadCat || loadSol}
            />
          </div>
        </Box>
      </div>

      {/* Cajón Resumen de Solo Lectura */}
      <CarritoResumen
        open={carritoOpen}
        onClose={() => setCarritoOpen(false)}
        carrito={formData.detalle}
        setFormData={() => {}}
        onCheckout={() => {}}
        isSaving={false}
        isReadOnly={true}
      />
    </ThemeProvider>
  )
}

export default BuscarSolicitudIngresoCliente
